// v1.0.12 — the stale-app guard.
//
// iOS Chrome was seen running app 1.0.5 on 29 Sep with 1.0.11 live: an old
// Claim link opened the index.html Chrome had cached with it, and that page
// names old (immutable, year-cached) assets, so the whole old app came back
// and stayed. The Worker line in Lab was right (asked live) while the App line
// was days old. A wallet can't depend on a browser's cache manners, so the app
// checks for itself: index.html carries its version in a <meta> (vite.config),
// and at start and on every return to the foreground the running app fetches
// "/" afresh (no-store), reads that stamp and, if it differs, reloads once at
// the next quiet moment — never under a slide or on a Drop page. Lab shows
// "live x.y.z" beside the App line while the two disagree.

const RUNNING = __APP_VERSION__;
const TRIED_KEY = "m.freshTried"; // the live version we already reloaded for (no loops)
const MIN_GAP_MS = 20_000; // a check within this of the last one is skipped (page load → login = one request)

let live: string | null = null;
let lastCheck = 0;
let retry = 0;

/** The version index.html says is live, when it differs from what's running (else null). */
export const liveVersion = (): string | null => (live && live !== RUNNING ? live : null);

function idle(): boolean {
  if (/^\/(c|d)\//.test(location.pathname)) return false; // a Drop's own pages (claim, the rest): never there
  if (/^\/embed\//.test(location.pathname)) return false; // 1.5.24 — M® Checkout's frame on a merchant's page: never (it lives minutes; the next order loads the new version)
  if (/^\/[0-9A-Za-z]{10}\/?$/.test(location.pathname)) return false; // v1.1.18 — a note's Take page: never there
  if (document.querySelector("#srDetails:not([hidden])")) return false; // past the keypad
  if (document.querySelector("#srOverlay:not([hidden])")) return false; // a success card / share
  if (document.querySelector(".slide.done")) return false; // a payment in flight
  if (document.activeElement instanceof HTMLInputElement) return false; // typing
  if (document.querySelector("[data-no-reload]")) return false; // v1.3.10 — a code or an unsaved key file is on screen (sparekey/lab.ts)
  return true;
}

function reloadWhenIdle() {
  window.clearTimeout(retry);
  if (!liveVersion()) return;
  let tried: string | null = null;
  try { tried = sessionStorage.getItem(TRIED_KEY); } catch { /* ignore */ }
  if (tried === live) return; // already reloaded for this one and still stale: Chrome kept its copy — leave it to Lab
  if (!idle()) {
    retry = window.setTimeout(reloadWhenIdle, 10_000);
    return;
  }
  try { sessionStorage.setItem(TRIED_KEY, live!); } catch { /* ignore */ }
  location.reload();
}

export async function checkFreshness(force = false): Promise<void> {
  if (!force && Date.now() - lastCheck < MIN_GAP_MS) return;
  lastCheck = Date.now();
  try {
    const res = await fetch(`/?fresh=${Date.now()}`, { cache: "no-store", headers: { accept: "text/html" } });
    if (!res.ok) return;
    const html = await res.text();
    const m = /name="m-app-version"\s+content="([^"]+)"/.exec(html);
    live = m?.[1] ?? null;
    if (liveVersion()) reloadWhenIdle();
  } catch {
    /* offline or blocked — nothing to do; the next foreground tries again */
  }
}

// app 1.4.0 — the deploy-cache trap (keys session, sync 13 Oct T2215Z §3).
// A tab opened just before a deploy holds an index.html whose lazily loaded
// chunks (the Spare Key screens, the notes code, …) the edge no longer has;
// Pages answers the missing chunk with the home page, `_headers` marks
// /assets/* immutable, and the screen fails with "'text/html' is not a valid
// JavaScript MIME type" until the tab is closed. Vite fires
// `vite:preloadError` when a chunk fails to load: reload once, which fetches
// the new index.html and so new chunk names (the poisoned URL is never asked
// for again). Not twice within a minute — a chunk that is really broken then
// fails as before instead of looping.
const CHUNK_KEY = "m.chunkReload";
function guardChunks(): void {
  window.addEventListener("vite:preloadError", (e) => {
    let last = 0;
    try { last = Number(sessionStorage.getItem(CHUNK_KEY) ?? "0"); } catch { /* ignore */ }
    if (Date.now() - last < 60_000) return; // let the error surface
    try { sessionStorage.setItem(CHUNK_KEY, String(Date.now())); } catch { /* ignore */ }
    e.preventDefault();
    location.reload();
  });
}

export function startFreshness(): void {
  guardChunks();
  void checkFreshness(true);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") void checkFreshness();
  });
  window.addEventListener("pageshow", (e) => {
    if ((e as PageTransitionEvent).persisted) void checkFreshness(true); // back from the bfcache
  });
}
